import { FC, useMemo, useState, type CSSProperties } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import type { PlaceItem, PlaceOpeningHoursEntry } from '../../../../../types/place.types';
import { TypeIcon } from '../../../../../components/Icons/TypeIcon';
import { ClockIcon } from '../../../../../components/Icons/ClockIcon';
import { GoogleLocationIcon } from '../../../../../components/Icons/GoogleLocationIcon';
import { LocationIcon } from '../../../../../components/Icons/LocationIcon';
import { formatTimeToAmPm } from '../../../../../utils/dateUtils';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import CustomButton from '../../../../../common/CustomButton';
import cardStyles from '../HotelResultCard/HotelResultCard.module.scss';
import styles from './PlaceResultCard.module.scss';

export interface PlaceResultCardProps {
  place: PlaceItem;
  categoryLabel: string;
  onSave: (place: PlaceItem) => void;
}

function buildMapsLink(place: PlaceItem): string | null {
  const lat = place.latitude;
  const lng = place.longitude;
  if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  const q = place.address?.trim() || place.short_address?.trim();
  if (q) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  }
  return null;
}

function formatRating(rating: number | undefined): string {
  if (rating == null || Number.isNaN(rating)) return '';
  return rating.toFixed(1);
}
function renderStars(rating: number | undefined) {
  const score = Math.max(0, Math.min(5, Math.round(rating ?? 0)));
  const stars = [];
  for (let i = 0; i < 5; i += 1) {
    stars.push(
      i < score ? (
        <StarRoundedIcon
          key={i}
          className={cardStyles.starFilled}
          fontSize="small"
        />
      ) : (
        <StarOutlineRoundedIcon
          key={i}
          className={cardStyles.starEmpty}
          fontSize="small"
        />
      )
    );
  }
  return stars;
}

function formatOpeningHoursTimesToAmPm(line: string): string {
  let s = line;
  s = s.replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d{3})?(?:Z|[+-]\d{2}:?\d{2})?/gi,
    match => {
      const d = new Date(match);
      return Number.isNaN(d.getTime()) ? match : formatTimeToAmPm(d);
    }
  );
  s = s.replace(/\b(\d{1,2}):(\d{2})\b(?!\s*[AaPp][Mm])/g, (match, hStr, mStr) => {
    const hour = parseInt(hStr, 10);
    const minute = parseInt(mStr, 10);
    if (hour > 23 || minute > 59) return match;
    const d = new Date(2000, 0, 1, hour, minute, 0, 0);
    return formatTimeToAmPm(d);
  });
  return s;
}

function summarizeOpeningHours(raw: PlaceOpeningHoursEntry[] | undefined): string {
  if (!raw?.length) return '';
  const first = raw[0];
  let joined: string;
  if (typeof first === 'string') {
    joined = first.trim();
  } else {
    const values = Object.values(first).filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
    if (!values.length) return '';
    joined = values.join(' · ');
  }
  return formatOpeningHoursTimesToAmPm(joined);
}

const PLACE_THUMB_EMPTY_BG = '#D4ECFF';

function placeThumbStyle(photoUrl: string | undefined): CSSProperties {
  const url = photoUrl?.trim();
  if (url) return { backgroundImage: `url(${url})` };
  return { backgroundColor: PLACE_THUMB_EMPTY_BG };
}

function shortDescription(text: string | undefined, maxLen = 180): string {
  if (!text?.trim()) return '';
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

const PlaceResultCard: FC<PlaceResultCardProps> = ({ place, categoryLabel, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();
  const ratingText = formatRating(place.rating);
  const addressText = place.short_address?.trim() || place.address?.trim() || '';
  const mapsLink = buildMapsLink(place);
  const hoursSummary = summarizeOpeningHours(place.opening_hours);
  const snippet = shortDescription(place.description);
  const displayTypeLabel = place.place_type_label?.trim() || categoryLabel.trim() || '';
  const reviewsSnippet = useMemo(() => {
    const n = place.user_ratings_total;
    if (n == null || n < 0) return '';
    const formatted = n.toLocaleString('de-DE');
    return `${formatted} ${n === 1 ? 'review' : 'reviews'}`;
  }, [place.user_ratings_total]);

  const thumbSx = useMemo(() => placeThumbStyle(place.photo_url), [place.photo_url]);

  const reviewsLabelDesktop = useMemo(() => {
    const n = place.user_ratings_total;
    if (n == null || n < 0) return '';
    const formatted = n.toLocaleString();
    return `${formatted} ${n === 1 ? 'review' : 'reviews'}`;
  }, [place.user_ratings_total]);

  const openLabel = place.is_open_now === true ? 'Open now' : place.is_open_now === false ? 'Closed now' : null;
  const toggleExpand = () => setExpanded(v => !v);
  const renderToggleControl = () => (
    <Box
      className={cardStyles.toggleRow}
      onClick={toggleExpand}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();

          toggleExpand();
        }
      }}
    >
      <Typography variant="h11">{expanded ? 'Show less' : 'Show more'}</Typography>

      <ExpandMoreIcon
        sx={{
          fontSize: 20,

          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',

          transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </Box>
  );

  const renderRatingDesktop = () =>
    (place.rating != null && place.rating > 0) || ratingText ? (
      <Box className={cardStyles.ratingRow}>
        <Box
          className={cardStyles.starsRow}
          aria-label={ratingText ? `Rating ${ratingText} of 5` : undefined}
        >
          {renderStars(place.rating)}
        </Box>

        {ratingText ? (
          <Typography
            component="span"
            className={cardStyles.ratingValue}
          >
            {ratingText}

            {reviewsLabelDesktop ? (
              <Typography
                component="span"
                variant="h11"
                sx={{ color: '#5E6A81', fontWeight: 400, ml: 0.5 }}
              >
                ({reviewsLabelDesktop})
              </Typography>
            ) : null}
          </Typography>
        ) : null}
      </Box>
    ) : null;

  const renderRatingMobile = () =>
    (place.rating != null && place.rating > 0) || ratingText ? (
      <Box className={cardStyles.ratingRowMobile}>
        <StarRoundedIcon
          className={cardStyles.starSingleMobile}
          fontSize="small"
          aria-hidden
        />

        {ratingText ? (
          <Typography
            component="span"
            className={cardStyles.ratingValueMobile}
          >
            {ratingText}
          </Typography>
        ) : null}

        {reviewsSnippet ? (
          <Typography
            component="span"
            className={cardStyles.reviewsMuted}
          >
            ({reviewsSnippet})
          </Typography>
        ) : null}
      </Box>
    ) : null;

  return (
    <Box className={cardStyles.card}>
      <Box
        className={`${cardStyles.summaryRow}${isMobile ? ` ${styles.placeSummaryRowMobile}` : ''}`}
      >
        {!isMobile ? (
          <Box
            className={`${cardStyles.thumb} ${styles.placeThumbDesktop}`}
            sx={thumbSx}
            aria-hidden
          />
        ) : (
          <Box className={styles.placeMobileLeftCol}>
            <Box
              className={`${cardStyles.thumb} ${styles.placeThumbDesktop}`}
              sx={thumbSx}
              aria-hidden
            />

            <Box className={styles.placeMobileTogglePush}>{renderToggleControl()}</Box>
          </Box>
        )}

        <Box className={`${cardStyles.summaryBody}${isMobile ? ` ${styles.placeMobileSummaryStretch}` : ''}`}>
          <Typography
            variant="h9Semibold"
            className={cardStyles.hotelName}
          >
            {place.name || '—'}
          </Typography>

          {isMobile ? renderRatingMobile() : renderRatingDesktop()}

          {displayTypeLabel ? (
            <Box className={styles.categoryRow}>
              <TypeIcon
                size={20}
                className={styles.categoryIcon}
              />
              <Typography
                component="span"
                variant="h11"
              >
                {displayTypeLabel}
              </Typography>
            </Box>
          ) : null}

          {addressText && isMobile ? (
            <Box className={cardStyles.addressRow}>
              <LocationIcon className={cardStyles.addressIcon} size={20} />

              <Typography
                component="span"
                className={cardStyles.addressText}
              >
                {addressText}
              </Typography>
            </Box>
          ) : null}

          {hoursSummary && !isMobile ? (
            <Box className={styles.hoursRow}>
              <ClockIcon
                size={20}
                className={styles.hoursIcon}
              />
              <Typography
                component="span"
                variant="h11"
              >
                {hoursSummary}
              </Typography>
            </Box>
          ) : null}

          {isMobile ? (
            <Box className={styles.placeMobileSaveInFlow}>
              <CustomButton
                variant="contained"
                btnType="button"
                onClick={() => onSave(place)}
                customClass={cardStyles.saveButtonMobile}
              >
                Save
              </CustomButton>
            </Box>
          ) : null}

          {!isMobile ? renderToggleControl() : null}
        </Box>

        {!isMobile ? (
          <Box className={cardStyles.asideColumn}>
            {openLabel ? (
              <Box className={place.is_open_now ? styles.openChip : styles.closedChip}>{openLabel}</Box>
            ) : (
              <Box aria-hidden />
            )}

            <Box className={cardStyles.actions}>
              <CustomButton
                variant="contained"
                btnType="button"
                onClick={() => onSave(place)}
                customClass={cardStyles.saveButton}
              >
                Save
              </CustomButton>
            </Box>
          </Box>
        ) : null}
      </Box>

      <Box className={cardStyles.detailsWrap}>
        <Collapse
          in={expanded}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={cardStyles.details}>
            <Typography
              variant="h9Bold"
              className={cardStyles.detailsHeading}
            >
              Details
            </Typography>

            {place.description ? (
              <Box className={cardStyles.detailBlock}>
                <Typography
                  component="span"
                  className={cardStyles.detailBlockLabel}
                >
                  Description
                </Typography>
                <Typography
                  component="p"
                  className={cardStyles.detailDescription}
                >
                  {place.description}
                </Typography>
              </Box>
            ) : null}
            <Box className={cardStyles.detailsDivider} />

            {addressText ? (
              <Box className={cardStyles.detailBlock}>
                <Typography
                  component="span"
                  className={cardStyles.detailBlockLabel}
                >
                  Address
                </Typography>

                <Typography
                  component="p"
                  className={cardStyles.detailAddressText}
                >
                  {addressText}
                </Typography>

                {mapsLink ? (
                  <a
                    className={cardStyles.mapsLink}
                    href={mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GoogleLocationIcon className={cardStyles.mapsLinkIcon} />

                    <span>Google Maps</span>
                  </a>
                ) : null}
              </Box>
            ) : null}

<Box className={cardStyles.detailsDivider} />

            {place.phone_number ? (
              <Box className={cardStyles.detailInlineRow}>
                <Typography
                  component="span"
                  className={cardStyles.detailInlineLabel}
                >
                  Phone
                </Typography>

                <Typography
                  component="span"
                  className={cardStyles.detailInlineValue}
                >
                  {place.phone_number}
                </Typography>
              </Box>
            ) : null}

            {place.website ? (
              <Box className={cardStyles.detailInlineRow}>
                <Typography
                  component="span"
                  className={cardStyles.detailInlineLabel}
                >
                  Link
                </Typography>

                <a
                  href={place.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardStyles.detailExternalLink}
                >
                  {place.website}
                </a>
              </Box>
            ) : null}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default PlaceResultCard;
